import { IKuikaDataType, IKuikaDataTypeField, IWorkFlowBaseInfo } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import _ from "lodash";
import { ChangeEvent, FC, KeyboardEvent as ReactKeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { ProjectDataTypesService } from "../../../../services/project-data-types-service";
import useLoadingPresentation, { type LoadingPresentation } from "../../../../shared/hooks/useLoadingPresentation";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { normalizeForSearch } from "../../../../shared/utility/search-normalize";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import { copyTextToClipboard } from "../../../ui-kit/kuika-copy-button/copy-text-to-clipboard";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaPopover from "../../../ui-kit/kuika-popover/kuika-popover";
import { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import {
  KuikaSkeletonForm,
  KuikaSkeletonList,
  KuikaSkeletonRegion
} from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaDataTypeDropdown from "../../shared/kuika-data-type-dropdown/kuika-data-type-dropdown";
import {
  emittedPropertyName,
  getFieldNameError,
  getTypeNameError,
  SCALAR_TYPE_OPTIONS
} from "../../shared/object-type-builder";
import { CatalogEntry, collectTypeUsage, describeTypeUsage, IDataTypeRename, TypeUsage } from "./data-type-references";
import "./project-data-types-manager-modal.scss";

/** Copy shown behind the Array header's info affordance — the design's exact wording. */
const ARRAY_INFO: string =
  "Turn on to store a collection of this type instead of a single value. Example: Customer.Company as single = " +
  "their current employer; as Array = their full employment history.";

/**
 * Icon per catalog scalar type. Every name is an asset in `public/images/common`; KuikaSvg masks a
 * missing file to nothing (fails silently), so this map is kept conservative and verified.
 */
const SCALAR_TYPE_ICONS: Record<string, string> = {
  String: "text-size",
  Boolean: "circle-half-vertical",
  Int32: "number-8",
  Int64: "number-8",
  Double: "number-8",
  Decimal: "number-8",
  DateTime: "calendar-event",
  Guid: "key"
};

interface ScalarTypeMeta {
  value: string;
  label: string;
  icon: string;
}

/**
 * The scalar options offered by the Type picker, derived from the builder's `SCALAR_TYPE_OPTIONS`
 * (the single source of the catalog field vocabulary — its values map to C# on the backend). This is
 * deliberately NOT the workflow parameter dropdown's vocabulary: the two must not be mixed.
 */
const SCALAR_TYPE_META: ScalarTypeMeta[] = SCALAR_TYPE_OPTIONS.map((option: KuikaSelectboxOption): ScalarTypeMeta => {
  const value: string = String(option.value);
  return { value, label: String(option.label), icon: SCALAR_TYPE_ICONS[value] ?? "code" };
});

const SCALAR_LABEL_BY_VALUE: Record<string, string> = SCALAR_TYPE_META.reduce(
  (accumulator: Record<string, string>, meta: ScalarTypeMeta): Record<string, string> => {
    accumulator[meta.value] = meta.label;
    return accumulator;
  },
  {} as Record<string, string>
);

/** The icon a field type reads with: a scalar's own glyph, a grid for a catalog reference, a pencil for empty. */
const getFieldTypeIcon = (type: string): string => {
  if (!type) return "edit";
  const scalarIcon: string | undefined = SCALAR_TYPE_ICONS[type];
  if (scalarIcon) return scalarIcon;
  if (SCALAR_LABEL_BY_VALUE[type]) return "code";
  return "components";
};

/** The human label for a field type: the scalar's label, the catalog name verbatim, or the placeholder. */
const getFieldTypeLabel = (type: string): string => {
  if (!type) return "Choose type..";
  return SCALAR_LABEL_BY_VALUE[type] ?? type;
};

interface FieldTypeCellProps {
  value: string;
  catalogNames: string[];
  onSelect: (type: string) => void;
  /** Array toggle for this field; when on, the trigger masks the type as `Type[]` (the stored type stays scalar). */
  isList: boolean;
}

/** Flush combobox trigger + popover hosting the Type picker, styled like the Process Parameters grid. */
const FieldTypeCell: FC<FieldTypeCellProps> = ({ value, catalogNames, onSelect, isList }) => {
  const [open, setOpen] = useState<boolean>(false);
  const hasValue: boolean = !!value;

  return (
    <KuikaPopover
      visible={open}
      onVisibleChange={setOpen}
      align="start"
      hideCloseIcon
      className="kuika-dt-fields__type-popover"
      content={
        <KuikaDataTypeDropdown
          value={value}
          primitives={SCALAR_TYPE_META}
          catalogNames={catalogNames}
          hideManage
          onChange={(type: string): void => onSelect(type)}
          onRequestClose={(): void => setOpen(false)}
        />
      }
    >
      <button
        type="button"
        className={classNames("kuika-dt-fields__type-trigger", { "kuika-dt-fields__type-trigger--empty": !hasValue })}
        data-testid="dataTypeFieldTypeTrigger"
      >
        <span className="kuika-dt-fields__type-value">
          <KuikaSvg name={getFieldTypeIcon(value)} width={14} height={14} color="var(--dark-5)" />
          <span className="kuika-dt-fields__type-text" data-testid="dataTypeFieldTypeText">
            {getFieldTypeLabel(value)}
            {hasValue && isList ? "[]" : ""}
          </span>
        </span>
        <KuikaSvg
          className="kuika-dt-fields__type-chevron"
          name={open ? "chevron-up" : "chevron-down"}
          type="arrows"
          width={12}
          height={12}
          color="var(--dark-5)"
        />
      </button>
    </KuikaPopover>
  );
};

interface FieldsEditorProps {
  fields: IKuikaDataTypeField[];
  ownerTypeName: string;
  catalogNames: string[];
  onChange: (fields: IKuikaDataTypeField[]) => void;
}

const EMPTY_FIELD: IKuikaDataTypeField = { name: "", type: "String", isList: false };

/** A unique field name whose emitted C# property does not collide with an existing field. */
const uniqueFieldName = (base: string, taken: Set<string>): string => {
  const seed: string = base || "Field";
  let candidate: string = `${seed}Copy`;
  let suffix: number = 2;
  while (taken.has(emittedPropertyName(candidate))) {
    candidate = `${seed}Copy${suffix}`;
    suffix += 1;
  }
  return candidate;
};

/** True while a text field / textarea holds focus, so list keyboard shortcuts never eat a keystroke. */
const isTextEntryFocused = (): boolean => {
  const element: Element | null = document.activeElement;
  if (!element) return false;
  const tag: string = element.tagName;
  if (tag === "TEXTAREA") return true;
  if ((element as HTMLElement).isContentEditable) return true;
  if (tag === "INPUT") {
    const inputType: string = (element as HTMLInputElement).type;
    return inputType !== "checkbox" && inputType !== "radio" && inputType !== "button";
  }
  return false;
};

/**
 * The Fields grid: a bordered table whose header band flips to a selection toolbar (Duplicate / Copy /
 * Delete, with ⌘D / ⌘C / ⌫) once rows are checked. A permanent trailing draft row adds new fields the
 * moment the user types — the pristine draft is stripped on save, so it never blocks CREATE. The row
 * shape produced is the same `{ name, type, isList }` the previous editor emitted, so persistence and
 * code generation are unchanged.
 */
const FieldsEditor: FC<FieldsEditorProps> = ({ fields, ownerTypeName, catalogNames, onChange }) => {
  const [selected, setSelected] = useState<Set<number>>(new Set<number>());

  const hasTrailingEmpty: boolean = fields.length > 0 && !fields[fields.length - 1].name;
  const displayFields: IKuikaDataTypeField[] = hasTrailingEmpty ? fields : [...fields, EMPTY_FIELD];
  const draftIndex: number = hasTrailingEmpty ? fields.length - 1 : fields.length;
  const selectableCount: number = hasTrailingEmpty ? fields.length - 1 : fields.length;

  const selectionActive: boolean = selected.size > 0;
  const allSelected: boolean = selectableCount > 0 && selected.size === selectableCount;
  const someSelected: boolean = selected.size > 0 && !allSelected;

  const updateRow = (index: number, patch: Partial<IKuikaDataTypeField>): void => {
    if (index < fields.length) {
      onChange(
        fields.map((field: IKuikaDataTypeField, i: number): IKuikaDataTypeField =>
          i === index ? { ...field, ...patch } : field
        )
      );
    } else {
      onChange([...fields, { ...EMPTY_FIELD, ...patch }]);
    }
  };

  const toggleRow = (index: number): void => {
    setSelected((previous: Set<number>): Set<number> => {
      const next: Set<number> = new Set<number>(previous);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleAll = (): void => {
    if (allSelected) {
      setSelected(new Set<number>());
      return;
    }
    const everything: Set<number> = new Set<number>();
    for (let i: number = 0; i < selectableCount; i += 1) everything.add(i);
    setSelected(everything);
  };

  const clearSelection = (): void => setSelected(new Set<number>());

  const orderedSelection = (): number[] =>
    [...selected]
      .filter((index: number): boolean => index < fields.length)
      .sort((a: number, b: number): number => a - b);

  const duplicateSelected = (): void => {
    const indices: number[] = orderedSelection();
    if (indices.length === 0) return;
    const taken: Set<string> = new Set<string>(
      fields
        .map((field: IKuikaDataTypeField): string => emittedPropertyName(field.name))
        .filter((name: string): boolean => !!name)
    );
    const clones: IKuikaDataTypeField[] = indices.map((index: number): IKuikaDataTypeField => {
      const source: IKuikaDataTypeField = fields[index];
      const name: string = uniqueFieldName(source.name, taken);
      taken.add(emittedPropertyName(name));
      return { ...source, name };
    });
    const insertAt: number = indices[indices.length - 1] + 1;
    onChange([...fields.slice(0, insertAt), ...clones, ...fields.slice(insertAt)]);
    clearSelection();
  };

  const copySelected = (): void => {
    const indices: number[] = orderedSelection();
    if (indices.length === 0) return;
    const payload: IKuikaDataTypeField[] = indices.map((index: number): IKuikaDataTypeField => {
      const source: IKuikaDataTypeField = fields[index];
      return { name: source.name, type: source.type, isList: !!source.isList };
    });
    // The shared write, which falls back to the textarea trick when the Clipboard API is refused.
    void copyTextToClipboard(JSON.stringify(payload, null, 2));
  };

  const deleteSelected = (): void => {
    const indices: number[] = orderedSelection();
    if (indices.length === 0) return;
    const removal: Set<number> = new Set<number>(indices);
    onChange(fields.filter((_field: IKuikaDataTypeField, i: number): boolean => !removal.has(i)));
    clearSelection();
  };

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (!selectionActive || isTextEntryFocused()) return;
    const isMeta: boolean = event.metaKey || event.ctrlKey;
    const key: string = event.key.toLowerCase();
    if (isMeta && key === "d") {
      event.preventDefault();
      duplicateSelected();
    } else if (isMeta && key === "c") {
      event.preventDefault();
      copySelected();
    } else if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      deleteSelected();
    }
  };

  const renderMasterCheckbox = (): JSX.Element => (
    <button
      type="button"
      aria-label={allSelected ? "Clear selection" : "Select all fields"}
      className={classNames("kuika-dt-fields__master", {
        "kuika-dt-fields__master--checked": allSelected,
        "kuika-dt-fields__master--indeterminate": someSelected
      })}
      onClick={toggleAll}
    >
      {allSelected && <KuikaSvg name="check" width={12} height={12} color="var(--white)" />}
      {someSelected && <KuikaSvg name="minus" width={12} height={12} color="var(--white)" />}
    </button>
  );

  const renderToolbar = (): JSX.Element => (
    <div className="kuika-dt-fields__toolbar">
      <div className="kuika-dt-fields__toolbar-left">
        {renderMasterCheckbox()}
        <span className="kuika-dt-fields__count">
          <strong>{selected.size}</strong> of {selectableCount} selected
        </span>
        <button type="button" className="kuika-dt-fields__clear" onClick={clearSelection}>
          <KuikaSvg name="x" width={12} height={12} color="var(--blue-5)" />
          <span>Clear Selection</span>
        </button>
      </div>
      <div className="kuika-dt-fields__toolbar-right">
        <button type="button" className="kuika-dt-fields__action" onClick={duplicateSelected}>
          <KuikaSvg name="copy-plus" width={14} height={14} color="var(--dark-7)" />
          <span>Duplicate</span>
          <span className="kuika-dt-fields__kbd">⌘D</span>
        </button>
        <button type="button" className="kuika-dt-fields__action" onClick={copySelected}>
          <KuikaSvg name="copy" width={14} height={14} color="var(--dark-7)" />
          <span>Copy</span>
          <span className="kuika-dt-fields__kbd">⌘C</span>
        </button>
        <button
          type="button"
          className="kuika-dt-fields__action kuika-dt-fields__action--danger"
          onClick={deleteSelected}
          data-testid="dataTypeFieldDeleteButton"
        >
          <KuikaSvg name="trash" width={14} height={14} color="var(--red-5)" />
          <span>Delete</span>
          <span className="kuika-dt-fields__kbd">⌫</span>
        </button>
      </div>
    </div>
  );

  const renderHead = (): JSX.Element => (
    <div className="kuika-dt-fields__head">
      <div className="kuika-dt-fields__head-cell">Name</div>
      <div className="kuika-dt-fields__head-cell">Type</div>
      <div className="kuika-dt-fields__head-cell kuika-dt-fields__head-cell--array">
        <span>Array</span>
        <KuikaTooltip content={ARRAY_INFO} position="top">
          <span className="kuika-dt-fields__info" tabIndex={0} aria-label="What does Array mean?">
            <KuikaSvg name="info-circle" width={14} height={14} color="var(--dark-5)" />
          </span>
        </KuikaTooltip>
      </div>
    </div>
  );

  return (
    <div className="kuika-dt-fields" onKeyDown={handleKeyDown}>
      {selectionActive ? renderToolbar() : renderHead()}
      <div className="kuika-dt-fields__body">
        {displayFields.map((field: IKuikaDataTypeField, index: number): JSX.Element => {
          const isDraft: boolean = index === draftIndex;
          const isSelected: boolean = selected.has(index);
          const rowError: string | null =
            !isDraft && field.name ? getFieldNameError(fields, index, ownerTypeName) : null;
          return (
            <div
              key={index}
              className={classNames("kuika-dt-fields__row", {
                "kuika-dt-fields__row--selected": isSelected,
                "kuika-dt-fields__row--checkable": selectionActive && !isDraft,
                "kuika-dt-fields__row--draft": isDraft
              })}
              data-testid={field.name ? `dataTypeFieldRow-${field.name}` : "dataTypeFieldDraftRow"}
            >
              <div
                className={classNames("kuika-dt-fields__cell", "kuika-dt-fields__cell--name", {
                  "kuika-dt-fields__cell--invalid": !!rowError
                })}
              >
                <div className="kuika-dt-fields__name-row">
                  {!isDraft && (
                    <span className="kuika-dt-fields__check">
                      <KuikaCheckbox
                        checked={isSelected}
                        onChange={(): void => toggleRow(index)}
                        dataTestId="dataTypeFieldRowCheckbox"
                      />
                    </span>
                  )}
                  <KuikaInput
                    className="kuika-dt-fields__name-input"
                    borderless
                    placeholder="Field name"
                    value={field.name}
                    onChange={(_event: ChangeEvent<HTMLInputElement>, value: string | number): void =>
                      updateRow(index, { name: String(value) })
                    }
                    data-testid="dataTypeFieldNameInput"
                  />
                </div>
                {/* The error is scoped to the name cell (the only validated column) so the red
                    invalid border encloses both the field and its reason, and the message sits
                    directly under the field it belongs to rather than spanning the whole row. */}
                {rowError && (
                  <div className="kuika-dt-fields__field-error" data-testid="dataTypeFieldNameError">
                    {rowError}
                  </div>
                )}
              </div>
              <div className="kuika-dt-fields__cell kuika-dt-fields__cell--type">
                <FieldTypeCell
                  value={field.type}
                  catalogNames={catalogNames}
                  onSelect={(type: string): void => updateRow(index, { type })}
                  isList={!!field.isList}
                />
              </div>
              <div className="kuika-dt-fields__cell kuika-dt-fields__cell--array">
                <KuikaSwitch
                  checked={!!field.isList}
                  onChange={(checked: boolean): void => updateRow(index, { isList: checked })}
                  dataTestId="dataTypeFieldArraySwitch"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** A catalog entry paired with its real index in `entries`, so search-filtered rows keep a stable id. */
interface CatalogListRow {
  entry: CatalogEntry;
  index: number;
}

export interface ProjectDataTypesManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called after a successful save so the host can refresh `sd.types`. `renames` lists the type
   * renames the save applied — the backend cascades those into every reference (other catalog
   * types' fields and workflow parameters), so the host must patch its in-memory workflow copies
   * to match, or its next whole-workflow save would silently revert the cascade.
   */
  onSaved?: (renames: IDataTypeRename[]) => void;
  /** All project workflows, so a type still referenced by a WF parameter is blocked from deletion. */
  workflows?: IWorkFlowBaseInfo[];
}

export const ProjectDataTypesManagerModal: FC<ProjectDataTypesManagerModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  workflows
}) => {
  const [entries, setEntries] = useState<CatalogEntry[]>([]);
  const [loadedNames, setLoadedNames] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  // Two waits that used to share one flag: fetching the catalog, and saving it. They lock different
  // things — the first load only has to keep the add buttons off a list it is about to replace;
  // the save holds the whole dialog.
  const [isCatalogLoading, setIsCatalogLoading] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<KuikaButtonStatus>("idle");
  const saveMachineRef = useRef<SaveStatus | null>(null);
  if (saveMachineRef.current === null) {
    saveMachineRef.current = new SaveStatus(setSaveStatus);
  }
  const saveMachine: SaveStatus = saveMachineRef.current;
  useEffect((): (() => void) => (): void => saveMachine.dispose(), [saveMachine]);
  const isSaving: boolean = saveStatus === "loading";
  const catalogPresentation: LoadingPresentation = useLoadingPresentation({
    isLoading: isCatalogLoading,
    hasData: entries.length > 0
  });
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  /** Index whose trash icon is awaiting confirmation; null keeps every confirm closed. */
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(null);

  useEffect((): (() => void) | void => {
    if (!isOpen) return;
    let cancelled: boolean = false;
    const load = async (): Promise<void> => {
      setIsCatalogLoading(true);
      try {
        const res = await ProjectDataTypesService.GetUserDataTypes();
        if (cancelled) return;
        const loaded: IKuikaDataType[] = Array.isArray(res.data) ? res.data : [];
        setEntries(
          loaded.map((dt: IKuikaDataType): CatalogEntry => ({ originalName: dt.name, dataType: _.cloneDeep(dt) }))
        );
        setLoadedNames(loaded.map((dt: IKuikaDataType): string => dt.name));
        setSelectedIndex(loaded.length > 0 ? 0 : -1);
      } catch (error: unknown) {
        KMainFunctions.exceptionHandler(error);
      } finally {
        if (!cancelled) setIsCatalogLoading(false);
      }
    };
    load();
    return (): void => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Reset the transient search (and any pending delete confirm) when the modal is
  // closed so it reopens in a clean state.
  useEffect((): void => {
    if (!isOpen) {
      setSearchOpen(false);
      setSearchQuery("");
      setConfirmDeleteIndex(null);
    }
  }, [isOpen]);

  const updateSelected = (patch: Partial<IKuikaDataType>): void => {
    setEntries((prev: CatalogEntry[]): CatalogEntry[] =>
      prev.map((e: CatalogEntry, i: number): CatalogEntry =>
        i === selectedIndex ? { ...e, dataType: { ...e.dataType, ...patch } } : e
      )
    );
  };

  const addType = (): void => {
    setEntries((prev: CatalogEntry[]): CatalogEntry[] => {
      const next: CatalogEntry[] = [
        ...prev,
        { originalName: undefined, dataType: { name: "", origin: "user", elements: [] } }
      ];
      setSelectedIndex(next.length - 1);
      return next;
    });
    setSearchOpen(false);
    setSearchQuery("");
  };

  const removeType = (index: number): void => {
    // Indices shift on removal, so a confirm left open on another row would point at the
    // wrong type afterwards.
    setConfirmDeleteIndex(null);
    setEntries((prev: CatalogEntry[]): CatalogEntry[] => {
      const next: CatalogEntry[] = prev.filter((_e, i: number): boolean => i !== index);
      setSelectedIndex(next.length === 0 ? -1 : Math.min(index, next.length - 1));
      return next;
    });
  };

  // Shared rules (pattern + case-insensitive uniqueness) with the entry itself excluded,
  // so retyping a type's own name in a different case is not a self-collision.
  const nameError = (name: string, index: number): string | null => {
    const otherTypeNames: string[] = entries
      .filter((_e: CatalogEntry, i: number): boolean => i !== index)
      .map((e: CatalogEntry): string => e.dataType.name || "");
    return getTypeNameError(name, otherTypeNames);
  };

  /** Named fields whose emitted property is unusable — an empty (draft) field is ignored, never an error. */
  const hasFieldError = (entry: CatalogEntry): boolean => {
    const fields: IKuikaDataTypeField[] = entry.dataType.elements ?? [];
    return fields.some(
      (field: IKuikaDataTypeField, i: number): boolean =>
        !!field.name && getFieldNameError(fields, i, entry.dataType.name) !== null
    );
  };

  const entryError = (entry: CatalogEntry, index: number): string | null => {
    const typeNameError: string | null = nameError(entry.dataType.name, index);
    if (typeNameError) return typeNameError;
    return hasFieldError(entry) ? "Some fields of this type are not valid." : null;
  };

  const isValid: boolean = entries.every((e: CatalogEntry, i: number): boolean => entryError(e, i) === null);

  const namedFieldCount = (entry: CatalogEntry): number =>
    (entry.dataType.elements ?? []).filter((field: IKuikaDataTypeField): boolean => !!field.name).length;

  const isRenamedEntry = (entry: CatalogEntry): boolean =>
    !!entry.originalName && !!entry.dataType.name && entry.originalName !== entry.dataType.name;

  const handleSave = async (): Promise<void> => {
    if (!isValid) return;
    // The commit is live again as soon as the ✓ replaces the spinner, so a second press would
    // re-run the whole save behind the closing dialog.
    if (!saveMachine.canStart) return;
    saveMachine.start();
    try {
      // Deletions: names present at load whose entry has since been removed. (A rename keeps the
      // entry — and its originalName — so it is an update, not a delete.)
      const survivingOriginalNames = new Set<string>(
        entries.map((e: CatalogEntry): string | undefined => e.originalName).filter((n): n is string => !!n)
      );
      const deletions: string[] = loadedNames.filter((name: string): boolean => !survivingOriginalNames.has(name));
      for (const name of deletions) {
        await ProjectDataTypesService.DeleteUserDataType(name);
      }

      // Renames this save applies. The backend cascades each into every stored reference (other
      // catalog types' fields, workflow parameters), so entries saved here must carry the NEW name
      // in their fields too — an entry still holding the old name would write it right back over
      // the cascade (KOA-18267). Renamed entries go first so the cascade lands before the rest.
      const renames: IDataTypeRename[] = entries
        .filter(isRenamedEntry)
        .map((e: CatalogEntry): IDataTypeRename => ({ from: e.originalName!, to: e.dataType.name }));
      // A field whose type equals a scalar option resolves to the SCALAR (scalar-first), never to a
      // same-named catalog type — so a rename FROM such a name must not rewrite fields. Workflow
      // parameters have no such ambiguity (`isObject` marks the catalog reference), so the full
      // rename list still goes to `onSaved`.
      const renameByOldName: Map<string, string> = new Map<string, string>(
        renames
          .filter((r: IDataTypeRename): boolean => !SCALAR_LABEL_BY_VALUE[r.from])
          .map((r: IDataTypeRename): [string, string] => [r.from, r.to])
      );
      const orderedEntries: CatalogEntry[] = [
        ...entries.filter(isRenamedEntry),
        ...entries.filter((e: CatalogEntry): boolean => !isRenamedEntry(e))
      ];

      for (const entry of orderedEntries) {
        // Strip pristine draft fields (empty name) before persisting — they never generate a property.
        const cleaned: IKuikaDataType = {
          ...entry.dataType,
          elements: (entry.dataType.elements ?? [])
            .filter((field: IKuikaDataTypeField): boolean => !!field.name)
            .map((field: IKuikaDataTypeField): IKuikaDataTypeField =>
              renameByOldName.has(field.type) ? { ...field, type: renameByOldName.get(field.type)! } : field
            )
        };
        if (entry.originalName) {
          await ProjectDataTypesService.UpdateUserDataType(entry.originalName, cleaned);
        } else {
          await ProjectDataTypesService.CreateUserDataType(cleaned);
        }
      }
      onSaved?.(renames);
      // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`. What changed
      // shows in the parameters behind it, so there is no toast to add.
      saveMachine.succeed(onClose);
    } catch (error: unknown) {
      saveMachine.fail();
      KMainFunctions.exceptionHandler(error);
    }
  };

  // KModal's `closeModal` calls through unconditionally, so the dialog guards its own close. Only a
  // save in flight holds it: walking away from a catalog that is still loading loses nothing.
  const handleClose = (): void => {
    if (isSaving) return;
    onClose();
  };

  const selected: CatalogEntry | undefined = selectedIndex >= 0 ? entries[selectedIndex] : undefined;
  const otherTypeNames: string[] = useMemo(
    (): string[] =>
      entries
        .filter((_e, i: number): boolean => i !== selectedIndex)
        .map((e: CatalogEntry): string => e.dataType.name)
        .filter((n: string): boolean => !!n),
    [entries, selectedIndex]
  );

  // Per-entry reference audit: which types / workflows still point at each type. Recomputed as the
  // catalog is edited (a reference removed in-session unblocks its target's delete straight away).
  const referenceMap = useMemo((): Map<number, TypeUsage> => {
    const projectWorkflows: IWorkFlowBaseInfo[] = workflows ?? [];
    const map: Map<number, TypeUsage> = new Map<number, TypeUsage>();
    entries.forEach((entry: CatalogEntry, index: number): void => {
      map.set(index, collectTypeUsage(entry, index, entries, projectWorkflows));
    });
    return map;
  }, [entries, workflows]);

  const visibleEntries = useMemo((): CatalogListRow[] => {
    const rows: CatalogListRow[] = entries.map((entry: CatalogEntry, index: number): CatalogListRow => ({
      entry,
      index
    }));
    // normalizeForSearch, not toLowerCase: a Turkish İ on either side ("MUSTERİ" typed
    // with caps, or a diacritic name in the catalog) breaks a plain lowercase match.
    const query: string = normalizeForSearch(searchQuery.trim());
    if (!query) return rows;
    return rows.filter((row: CatalogListRow): boolean =>
      normalizeForSearch(row.entry.dataType.name || "").includes(query)
    );
  }, [entries, searchQuery]);

  const renderListHead = (): JSX.Element => {
    if (searchOpen) {
      return (
        <div className="kuika-data-types-manager__list-head kuika-data-types-manager__list-head--searching">
          <KuikaInput
            className="kuika-data-types-manager__list-search"
            borderless
            autoFocus
            placeholder="Search types..."
            value={searchQuery}
            leftIcon={<KuikaSvg name="search" width={14} height={14} color="var(--dark-5)" />}
            onChange={(_event: ChangeEvent<HTMLInputElement>, value: string | number): void =>
              setSearchQuery(String(value))
            }
            data-testid="dataTypeSearchInput"
          />
          <button
            type="button"
            className="kuika-data-types-manager__list-icon-btn"
            aria-label="Close search"
            onClick={(): void => {
              setSearchOpen(false);
              setSearchQuery("");
            }}
          >
            <KuikaSvg name="x" width={14} height={14} color="var(--dark-5)" />
          </button>
        </div>
      );
    }
    return (
      <div className="kuika-data-types-manager__list-head">
        <span className="kuika-data-types-manager__list-title">Types</span>
        <div className="kuika-data-types-manager__list-actions">
          <button
            type="button"
            className="kuika-data-types-manager__list-icon-btn"
            aria-label="Search types"
            onClick={(): void => setSearchOpen(true)}
            data-testid="dataTypeSearchButton"
          >
            <KuikaSvg name="search" width={16} height={16} color="var(--dark-6)" />
          </button>
          <KuikaTooltip content="Add data type" position="top">
            <button
              type="button"
              className="kuika-data-types-manager__list-icon-btn"
              aria-label="Add data type"
              disabled={isCatalogLoading || isSaving}
              onClick={addType}
              data-testid="dataTypeAddButton"
            >
              <KuikaSvg name="plus" width={16} height={16} color="var(--dark-6)" />
            </button>
          </KuikaTooltip>
        </div>
      </div>
    );
  };

  const renderList = (): JSX.Element => (
    <aside className="kuika-data-types-manager__list">
      {renderListHead()}
      <div className="kuika-data-types-manager__list-scroll">
        {visibleEntries.length === 0 && (
          <div className="kuika-data-types-manager__list-none">No types match your search.</div>
        )}
        {visibleEntries.map(({ entry, index }): JSX.Element => {
          const invalid: boolean = entryError(entry, index) !== null;
          const isSelected: boolean = index === selectedIndex;
          const usage: TypeUsage | undefined = referenceMap.get(index);
          const blockReason: string | null = usage ? describeTypeUsage(usage) : null;
          const isReferenced: boolean = blockReason !== null;
          // Deleting cascades into the whole project on Save (the header says as much), so the
          // trash icon must not act on a single stray click (KOA-18293 B8). The row turns into
          // an in-place confirm instead of opening yet another modal over this one.
          if (confirmDeleteIndex === index) {
            return (
              <div
                key={index}
                className="kuika-data-types-manager__list-item kuika-data-types-manager__list-item--confirm"
                data-testid="dataTypeDeleteConfirm"
              >
                <span className="kuika-data-types-manager__confirm-text">
                  Delete “{entry.dataType.name || "Untitled Type"}”?
                </span>
                <span className="kuika-data-types-manager__confirm-actions">
                  <KuikaButton type="tertiary" size="sm" onClick={(): void => setConfirmDeleteIndex(null)}>
                    Cancel
                  </KuikaButton>
                  <KuikaButton
                    type="primary"
                    color="danger"
                    size="sm"
                    data-testid="dataTypeDeleteConfirmDelete"
                    onClick={(): void => {
                      setConfirmDeleteIndex(null);
                      removeType(index);
                    }}
                  >
                    Delete
                  </KuikaButton>
                </span>
              </div>
            );
          }
          return (
            <button
              type="button"
              key={index}
              className={classNames("kuika-data-types-manager__list-item", {
                "kuika-data-types-manager__list-item--active": isSelected,
                "kuika-data-types-manager__list-item--invalid": invalid
              })}
              data-testid="dataTypeListItem"
              onClick={(): void => setSelectedIndex(index)}
            >
              <KuikaSvg
                className="kuika-data-types-manager__list-item-icon"
                type="common"
                name="components"
                width={16}
                height={16}
              />
              <span className="kuika-data-types-manager__list-item-name" data-testid="dataTypeListItemName">
                {entry.dataType.name || "Untitled Type"}
              </span>
              <span className="kuika-data-types-manager__list-item-meta" data-testid="dataTypeListItemFieldCount">
                {namedFieldCount(entry)}
              </span>
              <KuikaTooltip content={blockReason ?? "Delete type"} position="top" contentTestId="dataTypeDeleteTooltip">
                <span
                  className={classNames("kuika-data-types-manager__list-item-remove", {
                    "kuika-data-types-manager__list-item-remove--disabled": isReferenced
                  })}
                  role="button"
                  data-testid="dataTypeListItemDeleteButton"
                  aria-label={blockReason ?? "Delete type"}
                  aria-disabled={isReferenced}
                  onClick={(e): void => {
                    e.stopPropagation();
                    if (isReferenced) return;
                    setConfirmDeleteIndex(index);
                  }}
                >
                  <KuikaSvg
                    type="common"
                    name="trash"
                    color={isReferenced ? "var(--dark-4)" : "var(--red-5)"}
                    width={15}
                    height={15}
                  />
                </span>
              </KuikaTooltip>
            </button>
          );
        })}
      </div>
    </aside>
  );

  const renderDetail = (): JSX.Element => {
    if (!selected) {
      return (
        <section className="kuika-data-types-manager__detail kuika-data-types-manager__detail--empty">
          <span>Select a type on the left, or add a new one.</span>
        </section>
      );
    }
    const err: string | null = nameError(selected.dataType.name, selectedIndex);
    const isRequiredHint: boolean = !selected.dataType.name;
    return (
      <section className="kuika-data-types-manager__detail">
        <div className="kuika-data-types-manager__field">
          <label className="kuika-data-types-manager__label">Name</label>
          <KuikaInput
            value={selected.dataType.name}
            placeholder="e.g. Customer"
            onChange={(_e: ChangeEvent<HTMLInputElement>, value: string | number): void =>
              updateSelected({ name: String(value) })
            }
            data-testid="dataTypeNameInput"
          />
          {err && (
            <div
              className={classNames("kuika-data-types-manager__hint", {
                "kuika-data-types-manager__hint--error": !isRequiredHint
              })}
              data-testid="dataTypeNameError"
            >
              {err}
            </div>
          )}
        </div>
        <div className="kuika-data-types-manager__field">
          <label className="kuika-data-types-manager__label">Fields</label>
          <FieldsEditor
            key={selectedIndex}
            fields={selected.dataType.elements ?? []}
            ownerTypeName={selected.dataType.name}
            catalogNames={otherTypeNames}
            onChange={(fields: IKuikaDataTypeField[]): void => updateSelected({ elements: fields })}
          />
        </div>
      </section>
    );
  };

  /**
   * The catalog body. While the FIRST load is in flight there is nothing truthful to show, and the
   * empty state would flash "No data types yet" over a project that has plenty — it also tore the
   * create button out from under a click as the response landed. So the first load previews the
   * list and the detail form (after the shared short wait; nothing at all before it), and a reopen
   * keeps its previous list on screen (real data, merely stale) with the add affordance disabled
   * until it is refreshed.
   */
  const renderBody = (): JSX.Element | null => {
    if (catalogPresentation === "pending") return null;
    if (catalogPresentation === "skeleton") {
      return (
        <div className="kuika-data-types-manager__split">
          <KuikaSkeletonRegion label="Loading data types" className="kuika-data-types-manager__list">
            <KuikaSkeletonList rows={6} density="compact" />
          </KuikaSkeletonRegion>
          {/* One announcement for the body: the list's region says it is loading; the detail's
              shapes are aria-hidden on their own. */}
          <KuikaSkeletonRegion
            announce={false}
            className="kuika-data-types-manager__skeleton-detail"
            data-testid="dataTypesSkeletonDetail"
          >
            <KuikaSkeletonForm fields={3} />
          </KuikaSkeletonRegion>
        </div>
      );
    }
    if (entries.length === 0) {
      return (
        <div className="kuika-data-types-manager__empty">
          <KuikaEmptyState
            variant="page"
            svgType="common"
            svgName="layers"
            customName="No data types yet"
            description="Define reusable object types once, then reference them from workflow parameters, datasources and screens."
            hideDescription={false}
          />
          {/* Same action as the list's "+", so it carries the same hook; both stay inert while the
              catalog request is in flight, because adding onto a list the response is about to
              replace loses the new entry. */}
          <KuikaButton disabled={isCatalogLoading || isSaving} onClick={addType} data-testid="dataTypeAddButton">
            Create your first data type
          </KuikaButton>
        </div>
      );
    }
    return (
      <div className="kuika-data-types-manager__split">
        {renderList()}
        {renderDetail()}
      </div>
    );
  };

  return (
    <KModal
      className="kuika-data-types-manager-modal"
      header="Kuika Data Types"
      showModal={isOpen}
      closeModal={handleClose}
      width={1100}
      centered
      isLoading={isSaving}
      footer={
        <KModalFooter
          submitLabel="Save"
          submitTestId="dataTypesManagerSaveButton"
          status={saveStatus}
          disabled={!isValid || isCatalogLoading}
          onSubmit={handleSave}
        >
          {/* Data types persist on the modal's own Save and cascade into every reference right away.
              Saying so prevents the reasonable assumption that the page's SAVE CHANGES / DISCARD
              covers them (KOA-18272). */}
          <KuikaSmallText className="kuika-data-types-manager-modal__footer-note">
            Save applies data types to the whole project immediately. The page's SAVE CHANGES / DISCARD does not affect
            them.
          </KuikaSmallText>
        </KModalFooter>
      }
    >
      <div className="kuika-data-types-manager" data-testid="dataTypesManagerModal">
        {/* KModal's header is a one-line caption, so the scope note sits at the top of the body. */}
        <KuikaSmallText className="kuika-data-types-manager__notice">
          Project-wide. Changes apply to every process and screen immediately.
        </KuikaSmallText>
        {renderBody()}
      </div>
    </KModal>
  );
};

export default ProjectDataTypesManagerModal;
